<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { authService } from '@/api/auth'
import { dashboardService, type AdminDashboardData } from '@/api/dashboard'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faUsers,
  faUserShield,
  faChartLine,
  faUserPlus,
  faHandHoldingUsd,
  faBoxOpen,
  faCogs,
  faShieldAlt,
  faBell,
  faChevronRight,
  faSync,
  faArrowTrendUp,
  faArrowTrendDown,
} from '@fortawesome/free-solid-svg-icons'
import WelcomeBanner from '@/components/farm/supervisor/WelcomeBanner.vue'
import AIProjectionCard from '@/components/ai/AIProjectionCard.vue'
import AIExecutiveBriefingModal from '@/components/ai/AIExecutiveBriefingModal.vue'

library.add(
  faUsers,
  faUserShield,
  faChartLine,
  faUserPlus,
  faHandHoldingUsd,
  faBoxOpen,
  faCogs,
  faShieldAlt,
  faBell,
  faChevronRight,
  faSync,
  faArrowTrendUp,
  faArrowTrendDown,
)

const authStore = useAuthStore()
const router = useRouter()
const loading = ref(true)
const dashboardData = ref<AdminDashboardData | null>(null)
const showExecutiveBriefing = ref(false)

const metrics = computed(() => [
  {
    name: 'Total Revenue',
    value: dashboardData.value?.metrics.totalRevenue.toLocaleString() || '0',
    change: dashboardData.value?.metrics.revenueChange || '+0%',
    trend: 'up',
    icon: 'hand-holding-usd',
    color: 'emerald',
    suffix: ' TZS'
  },
  {
    name: 'Net Profit',
    value: dashboardData.value?.metrics.netProfit.toLocaleString() || '0',
    change: dashboardData.value?.metrics.profitChange || '+0%',
    trend: 'up',
    icon: 'chart-line',
    color: 'blue',
    suffix: ' TZS'
  },
  {
    name: 'Livestock Count',
    value: dashboardData.value?.metrics.totalAnimals.toString() || '0',
    change: dashboardData.value?.metrics.animalsChange || '+0',
    trend: 'up',
    icon: 'box-open',
    color: 'purple',
    suffix: ''
  },
  {
    name: 'Active Fleet',
    value: dashboardData.value?.metrics.activeEquipment.toString() || '0',
    change: 'Stable',
    trend: 'neutral',
    icon: 'cogs',
    color: 'amber',
    suffix: ''
  },
])

const fetchDashboardData = async () => {
  try {
    loading.value = true
    dashboardData.value = await dashboardService.getAdminStats()
  } catch (err) {
    console.error('Failed to fetch admin stats:', err)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  if (!authStore.isAuthenticated || authStore.user?.role !== 'ADMIN') {
    router.push('/login')
    return
  }
  fetchDashboardData()
})

const quickActions = [
  { name: 'Manage Users', icon: 'users', path: '/admin/users', color: 'indigo' },
  { name: 'Financial Reports', icon: 'chart-line', path: '/admin/reports', color: 'emerald' },
  { name: 'System Messages', icon: 'bell', path: '/admin/messages', color: 'blue' },
  { name: 'Asset Logistics', icon: 'cogs', path: '/equipment', color: 'slate' },
]
</script>

<template>
  <div class="px-6 py-8 ml-0 transition-all duration-300 bg-slate-50/50 min-h-screen">
    <div class="max-w-7xl mx-auto">
      <WelcomeBanner />

      <!-- AI Farm Profit & Yield Projections Card -->
      <AIProjectionCard class="mb-8 mt-6" />

      <!-- Header Section -->
      <div class="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-10 mt-6 gap-6">
        <div>
          <h1 class="text-4xl font-black text-slate-900 tracking-tight">Executive Dashboard</h1>
          <p class="mt-2 text-slate-500 font-medium">Real-time enterprise performance metrics</p>
        </div>
        
        <div class="flex flex-wrap items-center gap-3">
          <button
            @click="showExecutiveBriefing = true"
            class="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold rounded-xl shadow-md shadow-indigo-500/20 transition-all cursor-pointer"
          >
            <span>✨</span>
            <span>AI Executive Briefing</span>
          </button>

          <button 
            @click="fetchDashboardData"
            class="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-600 font-bold rounded-xl hover:bg-slate-50 transition-all shadow-sm"
          >
            <FontAwesomeIcon icon="sync" :class="{ 'animate-spin': loading }" />
            Refresh
          </button>
          
          <router-link
            to="/admin/reports"
            class="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition-all shadow-lg shadow-slate-200"
          >
            <FontAwesomeIcon icon="chart-line" />
            Detailed Insights
          </router-link>
        </div>
      </div>

      <!-- High Impact Metrics -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <div 
          v-for="metric in metrics" 
          :key="metric.name"
          class="bg-white p-7 rounded-3xl border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group relative overflow-hidden"
        >
          <div :class="`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-${metric.color}-50 to-transparent rounded-bl-full opacity-50` "></div>
          
          <div class="relative z-10">
            <div :class="`w-14 h-14 rounded-2xl bg-${metric.color}-50 text-${metric.color}-600 flex items-center justify-center mb-6 shadow-inner`">
              <FontAwesomeIcon :icon="metric.icon" class="text-2xl" />
            </div>
            
            <p class="text-sm font-bold text-slate-400 uppercase tracking-widest mb-1">{{ metric.name }}</p>
            <div class="flex items-baseline gap-1">
              <h3 class="text-3xl font-black text-slate-900 tracking-tighter">
                {{ loading ? '...' : metric.value }}
              </h3>
              <span class="text-xs font-bold text-slate-400">{{ metric.suffix }}</span>
            </div>
            
            <div class="mt-6 flex items-center justify-between">
              <div class="flex items-center gap-1.5">
                <div :class="`px-2 py-0.5 rounded-lg text-xs font-black ${metric.trend === 'up' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-50 text-slate-500'}`">
                  <FontAwesomeIcon :icon="metric.trend === 'up' ? 'arrow-trend-up' : 'arrow-trend-down'" class="mr-1" />
                  {{ metric.change }}
                </div>
              </div>
              <span class="text-[10px] font-bold text-slate-300 uppercase">This Period</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Top Motivation & Production Champions Section -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
        <!-- Best Worker Card -->
        <div class="bg-gradient-to-br from-amber-500 via-amber-600 to-orange-600 rounded-3xl p-7 text-white shadow-xl shadow-amber-500/20 relative overflow-hidden group">
          <div class="absolute -right-8 -bottom-8 w-40 h-40 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
          
          <div class="relative z-10 flex flex-col justify-between h-full">
            <div class="flex items-center justify-between mb-6">
              <div class="flex items-center gap-2">
                <span class="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-black uppercase tracking-wider text-amber-100 flex items-center gap-1.5">
                  ⭐ Worker of the Period
                </span>
              </div>
              <span v-if="dashboardData?.topWorker" class="text-xs font-black bg-amber-300 text-slate-900 px-3 py-1 rounded-xl shadow-sm">
                {{ dashboardData.topWorker.composite_score }} / 100 PTS
              </span>
            </div>

            <div v-if="dashboardData?.topWorker" class="flex items-center gap-5 my-2">
              <div class="w-16 h-16 rounded-2xl bg-white text-amber-600 flex items-center justify-center font-black text-2xl shadow-lg shrink-0">
                {{ dashboardData.topWorker.full_legal_name.charAt(0) }}
              </div>
              <div>
                <h3 class="text-2xl font-black text-white tracking-tight leading-tight">
                  {{ dashboardData.topWorker.full_legal_name }}
                </h3>
                <p class="text-amber-100 font-semibold text-sm">
                  {{ dashboardData.topWorker.job_title }} • {{ dashboardData.topWorker.department }}
                </p>
                <div class="flex items-center gap-4 mt-2 text-xs font-bold text-amber-200">
                  <span>✓ {{ dashboardData.topWorker.tasks_completed }} Tasks Completed</span>
                  <span>•</span>
                  <span>⭐ {{ dashboardData.topWorker.avg_rating }} Rating</span>
                </div>
              </div>
            </div>
            <div v-else class="py-6 text-amber-100 font-semibold">
              No worker performance data recorded for this period yet.
            </div>

            <div class="mt-6 pt-5 border-t border-white/15 flex items-center justify-between">
              <span class="text-xs font-bold text-amber-100">Motivate & Award Outstanding Output</span>
              <router-link to="/workers/leaderboard" class="inline-flex items-center gap-2 px-4 py-2 bg-white text-slate-900 font-extrabold text-xs rounded-xl hover:bg-amber-50 transition-all shadow-md">
                Leaderboard →
              </router-link>
            </div>
          </div>
        </div>

        <!-- Best Department Card -->
        <div class="bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-800 rounded-3xl p-7 text-white shadow-xl shadow-indigo-500/20 relative overflow-hidden group">
          <div class="absolute -right-8 -bottom-8 w-40 h-40 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>

          <div class="relative z-10 flex flex-col justify-between h-full">
            <div class="flex items-center justify-between mb-6">
              <div class="flex items-center gap-2">
                <span class="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-black uppercase tracking-wider text-indigo-200 flex items-center gap-1.5">
                  🏆 Top Producing Department
                </span>
              </div>
              <span v-if="dashboardData?.topDepartment" class="text-xs font-black bg-indigo-300 text-slate-900 px-3 py-1 rounded-xl shadow-sm">
                {{ dashboardData.topDepartment.avg_score }} / 100 AVG
              </span>
            </div>

            <div v-if="dashboardData?.topDepartment" class="my-2">
              <h3 class="text-2xl font-black text-white tracking-tight leading-tight">
                {{ dashboardData.topDepartment.department }} Department
              </h3>
              <p class="text-indigo-200 font-semibold text-sm mt-1">
                Top Contributor: <span class="text-white font-bold">{{ dashboardData.topDepartment.top_worker_name }}</span>
              </p>
              <div class="grid grid-cols-2 gap-4 mt-4">
                <div class="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                  <p class="text-[10px] font-black uppercase text-indigo-200">Active Personnel</p>
                  <p class="text-lg font-black text-white">{{ dashboardData.topDepartment.worker_count }} Staff</p>
                </div>
                <div class="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                  <p class="text-[10px] font-black uppercase text-indigo-200">Execution Rate</p>
                  <p class="text-lg font-black text-white">{{ dashboardData.topDepartment.completion_rate }}% Tasks</p>
                </div>
              </div>
            </div>
            <div v-else class="py-6 text-indigo-200 font-semibold">
              No department production data recorded for this period yet.
            </div>

            <div class="mt-6 pt-5 border-t border-white/15 flex items-center justify-between">
              <span class="text-xs font-bold text-indigo-200">Department Performance Analytics</span>
              <router-link to="/workers/leaderboard" class="inline-flex items-center gap-2 px-4 py-2 bg-white text-indigo-900 font-extrabold text-xs rounded-xl hover:bg-indigo-50 transition-all shadow-md">
                Full Rankings →
              </router-link>
            </div>
          </div>
        </div>
      </div>


      <div class="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <!-- Main Activity Feed -->
        <div class="lg:col-span-8 bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden flex flex-col">
          <div class="p-8 border-b border-slate-50 flex items-center justify-between bg-slate-50/30">
            <div>
              <h3 class="text-xl font-black text-slate-900 flex items-center gap-2">
                <div class="w-1.5 h-6 bg-indigo-600 rounded-full"></div>
                Enterprise Flow
              </h3>
              <p class="text-sm text-slate-400 font-medium">Real-time system events</p>
            </div>
          </div>

          <div class="flex-1 overflow-y-auto max-h-[600px] scrollbar-hide">
            <div v-if="loading" class="p-20 text-center">
              <div class="inline-block animate-spin rounded-full h-10 w-10 border-4 border-indigo-600 border-t-transparent mb-4"></div>
              <p class="text-slate-400 font-bold">Aggregating system data...</p>
            </div>
            
            <div v-else-if="!dashboardData?.recentActivities.length" class="p-20 text-center text-slate-300">
              <FontAwesomeIcon icon="bell" class="text-6xl mb-4 opacity-10" />
              <p class="font-bold">No recent activities detected</p>
            </div>

            <div 
              v-else
              v-for="(act, idx) in dashboardData.recentActivities" 
              :key="act.id"
              class="p-8 hover:bg-slate-50/80 transition-all border-b border-slate-50 group flex items-center justify-between"
            >
              <div class="flex items-center gap-5 min-w-0">
                <div :class="`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 shadow-sm transition-all group-hover:scale-110 ${
                  act.type === 'sale' ? 'bg-emerald-50 text-emerald-600' : 
                  act.type === 'user' ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-50 text-slate-600'
                }`">
                  <FontAwesomeIcon :icon="act.type === 'sale' ? 'hand-holding-usd' : act.type === 'user' ? 'user-plus' : 'cog'" />
                </div>
                
                <div class="min-w-0">
                  <p class="font-black text-slate-800 text-lg tracking-tight truncate group-hover:text-indigo-600 transition-colors">
                    {{ act.action }}
                  </p>
                  <div class="flex items-center gap-4 mt-1.5">
                    <span class="text-xs font-bold text-slate-400 flex items-center gap-1.5">
                      <FontAwesomeIcon icon="users" class="opacity-50" />
                      {{ act.user }}
                    </span>
                    <span class="w-1 h-1 bg-slate-300 rounded-full"></span>
                    <span class="text-xs font-bold text-slate-300 uppercase tracking-widest">{{ act.time_str }}</span>
                  </div>
                </div>
              </div>
              
              <FontAwesomeIcon icon="chevron-right" class="text-slate-200 group-hover:text-indigo-300 group-hover:translate-x-2 transition-all" />
            </div>
          </div>
        </div>

        <!-- Sidebar Components -->
        <div class="lg:col-span-4 space-y-8">
          <!-- Quick Pulse Actions -->
          <div class="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm">
            <h3 class="text-xl font-black text-slate-900 mb-8 flex items-center gap-2">
              <div class="w-1.5 h-6 bg-amber-500 rounded-full"></div>
              Command Center
            </h3>
            
            <div class="grid grid-cols-1 gap-4">
              <router-link 
                v-for="action in quickActions" 
                :key="action.name"
                :to="action.path"
                class="group p-5 rounded-2xl bg-slate-50/50 hover:bg-white border border-transparent hover:border-slate-100 hover:shadow-xl transition-all duration-300 flex items-center gap-5"
              >
                <div :class="`w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center group-hover:bg-slate-900 group-hover:text-white transition-all` ">
                  <FontAwesomeIcon :icon="action.icon" />
                </div>
                <span class="font-bold text-slate-700 tracking-tight">{{ action.name }}</span>
                <FontAwesomeIcon icon="chevron-right" class="ml-auto text-slate-300 group-hover:text-slate-900 transition-colors" />
              </router-link>
            </div>
          </div>

          <!-- User Distribution Card -->
          <div class="bg-indigo-600 p-8 rounded-3xl shadow-2xl shadow-indigo-100 text-white relative overflow-hidden group">
            <div class="absolute -bottom-10 -right-10 w-48 h-48 bg-white/10 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-1000"></div>
            
            <div class="relative z-10">
              <div class="flex items-center justify-between mb-8">
                <h3 class="text-xl font-black">Force Status</h3>
                <FontAwesomeIcon icon="user-shield" class="text-2xl opacity-50" />
              </div>
              
              <div class="space-y-6">
                <div v-for="(count, role) in dashboardData?.userDistribution" :key="role">
                   <div class="flex justify-between text-xs font-black uppercase tracking-widest mb-2 text-indigo-100">
                     <span>{{ role }}</span>
                     <span>{{ count }}</span>
                   </div>
                   <div class="h-2 w-full bg-white/10 rounded-full overflow-hidden">
                     <div 
                      class="h-full bg-white shadow-[0_0_10px_rgba(255,255,255,0.5)]" 
                      :style="{ width: `${dashboardData?.metrics.totalUsers ? (count / dashboardData.metrics.totalUsers) * 100 : 0}%` }"
                     ></div>
                   </div>
                </div>
              </div>
              
              <div class="mt-10 pt-8 border-t border-white/10">
                <div class="flex items-center justify-between">
                  <div>
                    <p class="text-[10px] font-black uppercase text-indigo-300 tracking-tighter">Growth Capacity</p>
                    <p class="text-2xl font-black">{{ dashboardData?.metrics.totalUsers }}<span class="text-sm font-bold opacity-40">/1000</span></p>
                  </div>
                  <router-link to="/admin/users" class="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-xs font-black transition-colors uppercase">Optimize</router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- AI Executive Briefing Modal -->
    <AIExecutiveBriefingModal :is-open="showExecutiveBriefing" @close="showExecutiveBriefing = false" />
  </div>
</template>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
